import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { getThemeColors } from '../../theme/theme';

export interface PdfOptions {
  filename?: string;
  quality?: number;
  scale?: number;
  useCORS?: boolean;
  allowTaint?: boolean;
  backgroundColor?: string;
  removeElements?: string[];
}

export class ReportPdfGenerator {
  private static readonly DEFAULT_OPTIONS: PdfOptions = {
    filename: 'security-report.pdf',
    quality: 0.95,
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#11192c', // Custom dark blue background
    removeElements: []
  };

  /**
   * Detect if running on iOS (iPhone/iPad/iPod)
   */
  private static isIOS(): boolean {
    // Check UA string for iPhone/iPad/iPod, plus detect iPad on iOS 13+
    // which reports as "MacIntel" but has touch support
    return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.maxTouchPoints > 1 && /Macintosh/i.test(navigator.userAgent));
  }

  /**
   * Detect if running on any mobile device
   */
  private static isMobile(): boolean {
    return this.isIOS() || /Android/i.test(navigator.userAgent) ||
      (window.innerWidth <= 768);
  }

  /**
   * Generate PDF from HTML element
   */
  static async generatePdf(
    element: HTMLElement,
    options: PdfOptions = {}
  ): Promise<void> {
    const finalOptions = { ...this.DEFAULT_OPTIONS, ...options };
    
    // Declare mobile width override vars outside try/catch so cleanup works in both paths
    const isMobileDevice = this.isMobile();
    const originalWidth = element.style.width;
    const originalMinWidth = element.style.minWidth;
    const originalMaxWidth = element.style.maxWidth;
    const originalOverflow = element.style.overflow;

    try {
      console.log('🔄 PDF Generation Started');
      console.log('📊 Element dimensions:', {
        width: element.scrollWidth,
        height: element.scrollHeight,
        offsetWidth: element.offsetWidth,
        offsetHeight: element.offsetHeight
      });

      // Show loading state
      this.showLoadingState(element);

      // Check for map containers before dispatching event
      const mapContainers = element.querySelectorAll('[data-pdf-map-container="true"]');

      // Dispatch event to notify components about PDF export
      window.dispatchEvent(new CustomEvent('pdf-export-start'));

      // Only wait for map recreation if a map is actually present in the DOM
      if (mapContainers.length > 0) {
        console.log('🗺️ Map containers found, waiting for map readiness...');
        await new Promise<void>(resolve => {
          const onMapReady = () => {
            window.removeEventListener('pdf-map-ready', onMapReady);
            clearTimeout(fallbackTimer);
            console.log('🗺️ Map signaled ready');
            resolve();
          };
          window.addEventListener('pdf-map-ready', onMapReady);
          // Fallback: proceed after 3s even if map doesn't signal
          const fallbackTimer = setTimeout(() => {
            window.removeEventListener('pdf-map-ready', onMapReady);
            console.warn('🗺️ Map ready timeout - proceeding without map confirmation');
            resolve();
          }, 3000);
        });
      } else {
        console.log('🗺️ No map containers found - skipping map wait');
      }

      // Check for Mapbox canvases
      const mapboxCanvases = element.querySelectorAll('canvas.mapboxgl-canvas');
      console.log('🎨 Found Mapbox canvases:', mapboxCanvases.length);
      mapboxCanvases.forEach((canvas, index) => {
        const canvasEl = canvas as HTMLCanvasElement;
        console.log(`Canvas ${index}:`, {
          width: canvasEl.width,
          height: canvasEl.height,
          style: canvasEl.style.cssText,
          visible: canvasEl.offsetParent !== null
        });
      });

      // Temporarily hide elements that shouldn't be in PDF
      const hiddenElements = this.hideElementsForPdf(element, finalOptions.removeElements || []);
      console.log('🙈 Hidden elements for PDF:', hiddenElements.length);

      // Wait for images to load
      console.log('🖼️ Waiting for images to load...');
      await this.waitForImages(element);

      // Generate canvas from HTML
      console.log('🎨 Starting html2canvas with options:', {
        scale: finalOptions.scale,
        useCORS: finalOptions.useCORS,
        allowTaint: finalOptions.allowTaint,
        backgroundColor: finalOptions.backgroundColor,
        width: element.scrollWidth,
        height: element.scrollHeight
      });

      // Cap scale at 1.5 universally (~163 DPI for A4) — good quality for digital PDFs
      // and stays within iOS canvas memory limits (~16MP)
      const effectiveScale = Math.min(finalOptions.scale!, 1.5);

      // Force a consistent desktop-width capture (1200px) on mobile devices
      // so PDFs look the same regardless of device screen size
      const PDF_CAPTURE_WIDTH = 1200;

      if (isMobileDevice) {
        console.log(`📱 Mobile device detected — forcing element to ${PDF_CAPTURE_WIDTH}px width for consistent PDF capture`);
        element.style.width = `${PDF_CAPTURE_WIDTH}px`;
        element.style.minWidth = `${PDF_CAPTURE_WIDTH}px`;
        element.style.maxWidth = 'none';
        element.style.overflow = 'hidden';
        // Force browser reflow so layout recalculates at the new width
        void element.offsetHeight;
      }

      const captureWidth = element.scrollWidth;
      const captureHeight = element.scrollHeight;
      console.log(`📱 Capture dimensions: ${captureWidth}x${captureHeight}, scale: ${effectiveScale}`);

      const canvas = await html2canvas(element, {
        scale: effectiveScale,
        useCORS: finalOptions.useCORS,
        allowTaint: finalOptions.allowTaint,
        backgroundColor: finalOptions.backgroundColor,
        logging: false,
        width: captureWidth,
        scrollX: 0,
        scrollY: 0,
        windowWidth: captureWidth,
        windowHeight: captureHeight * 2,
        onclone: (clonedDoc) => {
          // Force the cloned document to desktop width for consistent layout
          const clonedBody = clonedDoc.body;
          clonedBody.style.width = `${captureWidth}px`;
          clonedBody.style.minWidth = `${captureWidth}px`;

          // Ensure the PDF container fills the full capture width
          const clonedContainer = clonedDoc.querySelector('[data-pdf-container="true"]') as HTMLElement;
          if (clonedContainer) {
            clonedContainer.setAttribute('data-pdf-capture', 'true');
            clonedContainer.style.maxWidth = 'none';
            clonedContainer.style.width = `${captureWidth}px`;
          }

          // Ensure all styles are applied to the cloned document
          this.applyStylesToClone(clonedDoc, element);

          // Clone Mapbox markers for PDF capture
          this.cloneMapboxMarkers(clonedDoc, element);
        }
      });

      console.log('✅ html2canvas completed. Canvas dimensions:', {
        width: canvas.width,
        height: canvas.height
      });

      // Create PDF
      const imgData = canvas.toDataURL('image/jpeg', finalOptions.quality);
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Calculate dimensions
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // Calculate scaling to fit page width (preserve original scaling)
      const widthRatio = pdfWidth / imgWidth;
      const scaledWidth = pdfWidth; // Always use full width
      const scaledHeight = imgHeight * widthRatio;

      console.log('📄 PDF dimensions calculated:', {
        originalCanvas: { width: imgWidth, height: imgHeight },
        pdfPage: { width: pdfWidth, height: pdfHeight },
        scaled: { width: scaledWidth, height: scaledHeight },
        willNeedMultiplePages: scaledHeight > pdfHeight
      });

      // Always split into multiple pages if content is taller than one page
      if (scaledHeight > pdfHeight) {
        console.log('📄 Creating multi-page PDF to preserve scaling');
        // Pass the source canvas directly to avoid base64 round-trip (helps iOS)
        await this.addMultiplePages(pdf, imgData, imgWidth, imgHeight, pdfWidth, pdfHeight, canvas);
      } else {
        // Single page - use full width
        console.log('📄 Creating single-page PDF');
        pdf.addImage(imgData, 'JPEG', 0, 0, scaledWidth, scaledHeight);
      }

      // Save the PDF - use native share sheet on mobile, direct download on desktop
      if (isMobileDevice) {
        const pdfBlob = pdf.output('blob');
        const pdfFile = new File([pdfBlob], finalOptions.filename!, { type: 'application/pdf' });

        // Try native share sheet first (iOS 15+, modern Android)
        if (navigator.share && navigator.canShare?.({ files: [pdfFile] })) {
          console.log('📱 Using native share sheet for PDF');
          await navigator.share({ files: [pdfFile], title: finalOptions.filename! });
        } else {
          // Fallback: navigate current tab to the PDF blob URL
          console.log('📱 Share API not available — opening PDF in current tab');
          const blobUrl = URL.createObjectURL(pdfBlob);
          window.location.href = blobUrl;
          setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
        }
      } else {
        pdf.save(finalOptions.filename!);
      }

      // Restore mobile element width override
      if (isMobileDevice) {
        element.style.width = originalWidth;
        element.style.minWidth = originalMinWidth;
        element.style.maxWidth = originalMaxWidth;
        element.style.overflow = originalOverflow;
      }

      // Restore hidden elements
      this.restoreHiddenElements(hiddenElements);
      this.hideLoadingState(element);

      // Dispatch event to notify components that PDF export is complete
      window.dispatchEvent(new CustomEvent('pdf-export-end'));

    } catch (error) {
      console.error('Error generating PDF:', error);
      // Restore mobile element width override on error
      if (isMobileDevice) {
        element.style.width = originalWidth;
        element.style.minWidth = originalMinWidth;
        element.style.maxWidth = originalMaxWidth;
        element.style.overflow = originalOverflow;
      }
      this.hideLoadingState(element);
      window.dispatchEvent(new CustomEvent('pdf-export-end'));
      throw new Error('Failed to generate PDF. Please try again.');
    }
  }

  /**
   * Add multiple pages to PDF for long content.
   * Accepts the source canvas directly to avoid re-loading large base64
   * data URLs into an Image (which can fail on iOS due to memory limits).
   */
  private static async addMultiplePages(
    pdf: jsPDF,
    imgData: string,
    imgWidth: number,
    imgHeight: number,
    pageWidth: number,
    pageHeight: number,
    sourceCanvas?: HTMLCanvasElement
  ): Promise<void> {
    // Calculate the scale factor from canvas to PDF
    const scaleFactor = pageWidth / imgWidth;

    // Calculate how many canvas pixels fit in one PDF page
    const canvasPageHeight = pageHeight / scaleFactor;

    // Use the source canvas directly if available (avoids base64 round-trip on iOS),
    // otherwise fall back to loading the data URL into an Image.
    let drawSource: HTMLCanvasElement | HTMLImageElement;

    if (sourceCanvas) {
      drawSource = sourceCanvas;
      console.log('📄 Using source canvas directly (avoids base64 round-trip)');
    } else {
      const sourceImg = new Image();
      await new Promise<void>((resolve, reject) => {
        sourceImg.onload = () => resolve();
        sourceImg.onerror = () => reject(new Error('Failed to load source image'));
        sourceImg.src = imgData;
      });
      drawSource = sourceImg;
    }

    // Calculate how many PDF pages we need
    const totalPages = Math.ceil(drawSource.height / canvasPageHeight);

    console.log(`📄 Splitting into ${totalPages} pages. Canvas: ${drawSource.width}x${drawSource.height}, PDF page: ${pageWidth}x${pageHeight}, scaleFactor: ${scaleFactor}, canvasPageHeight: ${canvasPageHeight}`);

    for (let i = 0; i < totalPages; i++) {
      if (i > 0) {
        pdf.addPage();
      }

      // Calculate which portion of the original canvas to use for this page
      const sourceY = i * canvasPageHeight;
      const sourceHeight = Math.min(canvasPageHeight, drawSource.height - sourceY);

      console.log(`📄 Page ${i + 1}/${totalPages}: sourceY=${sourceY}, sourceHeight=${sourceHeight}`);

      // Create a temporary canvas for this page
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');

      if (tempCtx) {
        // Set canvas size to match the source portion
        tempCanvas.width = drawSource.width;
        tempCanvas.height = sourceHeight;

        // Fill with background color first
        tempCtx.fillStyle = '#11192c'; // Custom dark blue background
        tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

        // Draw the portion of the source for this page
        tempCtx.drawImage(
          drawSource,
          0, sourceY, drawSource.width, sourceHeight, // Source rectangle
          0, 0, tempCanvas.width, tempCanvas.height    // Destination rectangle
        );

        // Calculate the height for this page in the PDF
        const pdfPageHeight = sourceHeight * scaleFactor;

        // Pass canvas directly to jsPDF (avoids expensive toDataURL base64 encoding)
        // 'FAST' compression flag speeds up the internal encoding
        pdf.addImage(tempCanvas, 'JPEG', 0, 0, pageWidth, pdfPageHeight, undefined, 'FAST');

        console.log(`📄 Added page ${i + 1} to PDF: width=${pageWidth}, height=${pdfPageHeight}`);
      }
    }

    console.log(`📄 Multi-page PDF creation complete: ${totalPages} pages`);
  }

  /**
   * Wait for all images in the element to load
   */
  private static waitForImages(element: HTMLElement): Promise<void> {
    const images = element.querySelectorAll('img');
    const perImageTimeout = 3000;
    const globalTimeout = 8000;

    // Force lazy-loaded images to start loading immediately
    Array.from(images).forEach((img) => {
      if (img.loading === 'lazy') {
        img.loading = 'eager';
        if (!img.complete && img.src) {
          const currentSrc = img.src;
          img.src = '';
          img.src = currentSrc;
        }
      }
    });

    const imagePromises = Array.from(images).map((img) => {
      if (img.complete && img.naturalWidth > 0) {
        return Promise.resolve();
      }

      return new Promise<void>((resolve) => {
        img.onload = () => resolve();
        img.onerror = () => resolve(); // Continue even if image fails to load
        setTimeout(() => resolve(), perImageTimeout);
      });
    });

    // Cap total wait at globalTimeout regardless of individual image count
    return Promise.race([
      Promise.all(imagePromises).then(() => {}),
      new Promise<void>(resolve => setTimeout(resolve, globalTimeout))
    ]);
  }

  /**
   * Clone Mapbox markers for PDF capture
   */
  private static cloneMapboxMarkers(clonedDoc: Document, originalElement: HTMLElement): void {
    console.log('🗺️ Cloning Mapbox markers for PDF capture');
    
    try {
      // Find all map containers with the PDF marker attribute
      const clonedMapContainers = clonedDoc.querySelectorAll('[data-pdf-map-container="true"]');
      console.log('🗺️ Found cloned map containers with PDF attribute:', clonedMapContainers.length);
      
      if (clonedMapContainers.length === 0) {
        console.log('🗺️ No map containers found in cloned document');
        return;
      }
      
      // Try to get marker data from window object (set by MapboxMap component)
      const markerData = (window as any).__mapboxMarkerData;
      console.log('🗺️ Found marker data on window:', markerData);
      
      if (markerData && markerData.length > 0) {
        // Use the marker data to create simple visual markers
        this.createMarkersFromData(clonedDoc, clonedMapContainers[0] as HTMLElement, markerData);
      } else {
        // Fallback to DOM-based cloning
        this.cloneMarkersFromDOM(clonedDoc, originalElement, clonedMapContainers);
      }
      
    } catch (error) {
      console.error('🗺️ Error in cloneMapboxMarkers:', error);
    }
  }

  /**
   * Create markers from data stored on window object
   */
  private static createMarkersFromData(clonedDoc: Document, mapContainer: HTMLElement, markerData: any[]): void {
    console.log('🗺️ Creating markers from data:', markerData.length);
    
    // Create a marker overlay container
    const markerOverlay = clonedDoc.createElement('div');
    markerOverlay.style.position = 'absolute';
    markerOverlay.style.top = '0';
    markerOverlay.style.left = '0';
    markerOverlay.style.width = '100%';
    markerOverlay.style.height = '100%';
    markerOverlay.style.pointerEvents = 'none';
    markerOverlay.style.zIndex = '1000';
    
    // Try to get map state from window object for coordinate conversion
    const mapState = (window as any).__mapboxMapState;
    console.log('🗺️ Found map state:', mapState);
    
    markerData.forEach((marker, index) => {
      try {
        // Create a marker wrapper
        const markerWrapper = clonedDoc.createElement('div');
        markerWrapper.style.position = 'absolute';
        markerWrapper.style.transform = 'translate(-50%, -50%)';
        
        // Create the visual marker
        const markerElement = clonedDoc.createElement('div');
        markerElement.style.display = 'flex';
        markerElement.style.alignItems = 'center';
        markerElement.style.justifyContent = 'center';
        
        // Determine marker style based on type
        if (marker.markerType === 'house') {
          // House icon for sites
          markerElement.innerHTML = '🏠';
          markerElement.style.fontSize = '24px';
          markerElement.style.width = '30px';
          markerElement.style.height = '30px';
          markerElement.style.backgroundColor = 'white';
          markerElement.style.borderRadius = '50%';
          markerElement.style.border = '2px solid ' + (marker.color || '#87CEEB');
          markerElement.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
        } else {
          // Colored circle for reports
          markerElement.style.width = '20px';
          markerElement.style.height = '20px';
          markerElement.style.backgroundColor = marker.color || '#2196F3';
          markerElement.style.borderRadius = '50%';
          markerElement.style.border = '2px solid white';
          markerElement.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
        }
        
        // Calculate position based on coordinates if we have map state
        let x, y;
        const mapRect = mapContainer.getBoundingClientRect();
        
        if (mapState && mapState.center && mapState.zoom && marker.latitude && marker.longitude) {
          // Try to convert lat/lng to pixel coordinates
          try {
            // Simple mercator projection approximation
            const centerLat = mapState.center.lat;
            const centerLng = mapState.center.lng;
            const zoom = mapState.zoom;
            
            // Calculate relative position from center
            const latDiff = marker.latitude - centerLat;
            const lngDiff = marker.longitude - centerLng;
            
            // Convert to pixels (rough approximation)
            const scale = Math.pow(2, zoom) * 256 / 360;
            const pixelX = lngDiff * scale;
            const pixelY = -latDiff * scale; // Negative because screen Y increases downward
            
            // Position relative to map center
            x = mapRect.width / 2 + pixelX;
            y = mapRect.height / 2 + pixelY;
            
            // Clamp to map bounds
            x = Math.max(10, Math.min(mapRect.width - 10, x));
            y = Math.max(10, Math.min(mapRect.height - 10, y));
            
            console.log(`🗺️ Converted coordinates for marker ${index + 1}: lat=${marker.latitude}, lng=${marker.longitude} -> x=${x}, y=${y}`);
          } catch (error) {
            console.error(`🗺️ Error converting coordinates for marker ${index + 1}:`, error);
            // Fall back to grid positioning
            x = mapRect.width / 2 + (index % 2 === 0 ? -50 : 50);
            y = mapRect.height / 2 + (Math.floor(index / 2) * 40 - 20);
          }
        } else {
          // Fall back to simple positioning when coordinates are the same or no map state
          console.log(`🗺️ Using fallback positioning for marker ${index + 1}`);
          
          // Check if all markers have the same coordinates
          const allSameCoords = markerData.every(m => 
            m.latitude === markerData[0].latitude && m.longitude === markerData[0].longitude
          );
          
          if (allSameCoords && markerData.length > 1) {
            // Stack markers with slight offsets when they're at the same location
            const centerX = mapRect.width / 2;
            const centerY = mapRect.height / 2;
            const offsetRadius = 30;
            const angle = (index / markerData.length) * 2 * Math.PI;
            
            x = centerX + Math.cos(angle) * offsetRadius;
            y = centerY + Math.sin(angle) * offsetRadius;
          } else {
            // Simple grid layout
            const cols = Math.min(3, markerData.length);
            const col = index % cols;
            const row = Math.floor(index / cols);
            
            const padX = mapRect.width * 0.2;
            const padY = mapRect.height * 0.2;
            const availWidth = mapRect.width - (2 * padX);
            const availHeight = mapRect.height - (2 * padY);
            
            x = padX + (availWidth / Math.max(cols - 1, 1)) * col;
            y = padY + (availHeight / Math.max(Math.ceil(markerData.length / cols) - 1, 1)) * row;
          }
        }
        
        markerWrapper.style.left = `${x}px`;
        markerWrapper.style.top = `${y}px`;
        
        markerWrapper.appendChild(markerElement);
        markerOverlay.appendChild(markerWrapper);
        
        console.log(`🗺️ Created marker ${index + 1} from data at position (${x}, ${y})`);
        
      } catch (error) {
        console.error(`🗺️ Error creating marker ${index + 1} from data:`, error);
      }
    });
    
    // Ensure the map container has relative positioning
    mapContainer.style.position = 'relative';
    
    // Append overlay to map container
    mapContainer.appendChild(markerOverlay);
    
    console.log('🗺️ Added marker overlay with all markers');
  }

  /**
   * Fallback: Clone markers from DOM elements
   */
  private static cloneMarkersFromDOM(clonedDoc: Document, originalElement: HTMLElement, clonedMapContainers: NodeListOf<Element>): void {
    console.log('🗺️ Falling back to DOM-based marker cloning');
    
    // Find all Mapbox marker elements in the original document
    const originalMarkers = originalElement.querySelectorAll('.mapboxgl-marker');
    console.log('🗺️ Found original markers:', originalMarkers.length);
    
    if (originalMarkers.length === 0) {
      console.log('🗺️ No Mapbox markers found to clone');
      return;
    }
    
    // For each original marker, create a simple visual representation
    originalMarkers.forEach((originalMarker, index) => {
      try {
        const markerElement = originalMarker as HTMLElement;
        const mapContainer = clonedMapContainers[0] as HTMLElement;
        
        // Create a simple marker representation
        const clonedMarker = clonedDoc.createElement('div');
        clonedMarker.className = 'pdf-marker-fallback';
        
        // Check if it's a house marker or regular marker
        const isHouseMarker = markerElement.innerHTML.includes('🏠') || markerElement.innerHTML.includes('house');
        
        if (isHouseMarker) {
          clonedMarker.innerHTML = '🏠';
          clonedMarker.style.fontSize = '24px';
          clonedMarker.style.color = '#87CEEB';
        } else {
          clonedMarker.innerHTML = '●';
          clonedMarker.style.fontSize = '20px';
          clonedMarker.style.color = '#F44336'; // Red for incidents
        }
        
        // Position in center with slight offset
        const mapRect = mapContainer.getBoundingClientRect();
        const centerX = mapRect.width / 2;
        const centerY = mapRect.height / 2;
        const offsetX = (index % 3 - 1) * 25;
        const offsetY = Math.floor(index / 3) * 25 - 12;
        
        clonedMarker.style.position = 'absolute';
        clonedMarker.style.left = `${centerX + offsetX}px`;
        clonedMarker.style.top = `${centerY + offsetY}px`;
        clonedMarker.style.transform = 'translate(-50%, -50%)';
        clonedMarker.style.zIndex = '1000';
        clonedMarker.style.pointerEvents = 'none';
        
        // Ensure the map container has relative positioning
        mapContainer.style.position = 'relative';
        
        // Append to map container
        mapContainer.appendChild(clonedMarker);
        
        console.log(`🗺️ Created fallback marker ${index + 1} at position (${centerX + offsetX}, ${centerY + offsetY})`);
        
      } catch (error) {
        console.error(`🗺️ Error creating fallback marker ${index + 1}:`, error);
      }
    });
  }

  /**
   * Apply styles to cloned document for better rendering
   */
  private static applyStylesToClone(clonedDoc: Document, originalElement: HTMLElement): void {
    // Copy computed styles for better rendering
    const originalStyles = window.getComputedStyle(originalElement);
    const clonedElement = clonedDoc.body.querySelector('[data-html2canvas-clone]') as HTMLElement;
    
    if (clonedElement) {
      // Apply key styles that might be lost
      clonedElement.style.fontFamily = originalStyles.fontFamily;
      clonedElement.style.fontSize = originalStyles.fontSize;
      clonedElement.style.lineHeight = originalStyles.lineHeight;
      clonedElement.style.color = originalStyles.color;
      clonedElement.style.backgroundColor = originalStyles.backgroundColor;
      
      // Ensure images are visible but preserve their CSS dimensions and object-fit
      const images = clonedElement.querySelectorAll('img');
      images.forEach((img) => {
        img.style.maxWidth = '100%';
      });
    }
  }

  /**
   * Hide elements that shouldn't appear in PDF
   */
  private static hideElementsForPdf(element: HTMLElement, selectors: string[]): Array<{ element: HTMLElement; originalDisplay: string }> {
    const hiddenElements: Array<{ element: HTMLElement; originalDisplay: string }> = [];
    
    // Default elements to hide
    const defaultSelectors = [
      'button',
      '.MuiButton-root',
      '[role="button"]',
      '.export-button',
      '.edit-button',
      '.action-button'
    ];
    
    const allSelectors = [...defaultSelectors, ...selectors];
    
    allSelectors.forEach(selector => {
      const elements = element.querySelectorAll(selector) as NodeListOf<HTMLElement>;
      elements.forEach(el => {
        hiddenElements.push({
          element: el,
          originalDisplay: el.style.display
        });
        el.style.display = 'none';
      });
    });
    
    return hiddenElements;
  }

  /**
   * Restore previously hidden elements
   */
  private static restoreHiddenElements(hiddenElements: Array<{ element: HTMLElement; originalDisplay: string }>): void {
    hiddenElements.forEach(({ element, originalDisplay }) => {
      element.style.display = originalDisplay;
    });
  }

  /**
   * Show loading state
   */
  private static showLoadingState(element: HTMLElement): void {
    const loadingOverlay = document.createElement('div');
    loadingOverlay.id = 'pdf-loading-overlay';
    loadingOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      color: white;
      font-size: 18px;
      font-family: Arial, sans-serif;
    `;
    loadingOverlay.innerHTML = `
      <div style="text-align: center;">
        <div style="margin-bottom: 10px;">Generating PDF...</div>
        <div style="width: 40px; height: 40px; border: 4px solid #f3f3f3; border-top: 4px solid #3498db; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto;"></div>
      </div>
      <style>
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      </style>
    `;
    
    document.body.appendChild(loadingOverlay);
  }

  /**
   * Hide loading state
   */
  private static hideLoadingState(element: HTMLElement): void {
    const loadingOverlay = document.getElementById('pdf-loading-overlay');
    if (loadingOverlay) {
      loadingOverlay.remove();
    }
  }

  /**
   * Generate PDF with optimized settings for reports
   */
  static async generateReportPdf(
    element: HTMLElement,
    reportTitle: string = 'Security Report'
  ): Promise<void> {
    const filename = `${reportTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().toISOString().split('T')[0]}.pdf`;
    
    // Use the custom dark blue background for consistency
    const backgroundColor = '#11192c'; // Custom dark blue background
    
    return this.generatePdf(element, {
      filename,
      quality: 0.95,
      scale: 2,
      backgroundColor,
      removeElements: [
        '.export-buttons',
        '.edit-controls',
        '.action-buttons',
        '[data-exclude-from-pdf="true"]'
      ]
    });
  }
}
